// src/components/ScreenshotEditor.js
import React, {
  useState,
  useRef,
  useCallback,
  useEffect
} from "react";
import Cropper from "react-easy-crop";
import { getCroppedImg } from "../../utils/cropImage";
import "./ScreenshotEditor.css";

export default function ScreenshotEditor({ image, onCancel, onSave }) {
  const [mode, setMode] = useState("none"); // 'none'|'draw'|'erase'|'crop'
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  // ── new drawing/erasing options ──
  const [strokeColor, setStrokeColor]   = useState("#FF0000"); // default red
  const [strokeSize,  setStrokeSize]    = useState(4);        // default radius 4px
  const [eraserSize,  setEraserSize]    = useState(12);       // default 12px square

  const containerRef = useRef(null);
  const canvasRef    = useRef(null);

  // Stretch canvas to container size
  useEffect(() => {
    const c = containerRef.current;
    const cv = canvasRef.current;
    if (!c || !cv) return;
    const { width, height } = c.getBoundingClientRect();
    cv.width  = width;
    cv.height = height;
    cv.style.width  = `${width}px`;
    cv.style.height = `${height}px`;
  }, []);

  const onCropComplete = useCallback((_, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const startDrawing = () => {
    if (mode === "draw" || mode === "erase") setIsDrawing(true);
  };
  const stopDrawing = () => {
    if (mode === "draw" || mode === "erase") setIsDrawing(false);
  };

  const draw = e => {
    if (!isDrawing) return;
    const ctx = canvasRef.current.getContext("2d");
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (mode === "draw") {
      ctx.fillStyle = strokeColor;
      ctx.beginPath();
      ctx.arc(x, y, strokeSize, 0, 2 * Math.PI);
      ctx.fill();
    } else {
      const size = eraserSize;
      ctx.clearRect(x - size/2, y - size/2, size, size);
    }
  };

  const handleSave = async () => {
    // 1) Get the base image (raw or cropped)
    let baseDataUrl = image;
    if (mode === "crop") {
      baseDataUrl = await getCroppedImg(image, croppedAreaPixels);
    }

    // 2) Load it into an <img> so we know its intrinsic dimensions
    const img = new Image();
    img.src = baseDataUrl;
    await new Promise((resolve) => (img.onload = resolve));

    // 3) Create a new off-screen canvas at the image’s size
    const merged = document.createElement("canvas");
    merged.width = img.width;
    merged.height = img.height;
    const mctx = merged.getContext("2d");

    // 4) Draw the base image
    mctx.drawImage(img, 0, 0);

    // 5) Draw your drawing layer on top, scaling it to match if needed
    const drawCanvas = canvasRef.current;
    // assume drawCanvas is same CSS size as container; adjust if you used different sizing
    mctx.drawImage(drawCanvas, 0, 0, merged.width, merged.height);

    // 6) Export the composite as PNG and send it back
    const finalDataUrl = merged.toDataURL("image/png");
    onSave(finalDataUrl);
  };

  return (
    <div className="screenshot-editor-overlay">
      <div className="screenshot-editor-content" ref={containerRef}>

        {/* Cropper */}
        <div
          style={{
            position: "absolute",
            top:0,left:0,right:0,bottom:0,
            pointerEvents: mode==="crop" ? "auto" : "none",
            zIndex:1
          }}
        >
          <Cropper
            image={image}
            crop={crop}
            zoom={zoom}
            aspect={16/9}
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            style={{
              containerStyle: { width:"100%",height:"100%" },
              cropAreaStyle:   { border:"2px solid #fff" }
            }}
          />
        </div>

        {/* Drawing / Eraser Canvas */}
        <canvas
          ref={canvasRef}
          onMouseDown={startDrawing}
          onMouseUp={stopDrawing}
          onMouseMove={draw}
          style={{
            position: "absolute",
            top: 0, left: 0,
            zIndex:2,
            pointerEvents:
              mode==="draw"||mode==="erase" ? "auto" : "none",
            cursor:
              mode === "erase"
              ? "not-allowed"
              : mode === "draw"
              ? "crosshair"
              : "default"
          }}
        />

        {/* ── Color picker ── */}
        <div className="color-picker">
          {[
            { name:"Red",    hex:"#FF0000" },
            { name:"Green",  hex:"#00CC00" },
            { name:"Yellow", hex:"#FFFF00" },
            { name:"LightBlue", hex:"#00CCFF" },
          ].map(({ name, hex }) => (
            <button
              key={name}
              className={strokeColor === hex ? "selected" : ""}
              style={{ backgroundColor: hex }}
              onClick={()=>setStrokeColor(hex)}
              title={name}
            />
          ))}
        </div>

        {/* ── Size sliders ── */}
        <div className="size-picker">
          <label>
            Stroke size:
            <input
              className="size-range"
              type="range"
              min={1} max={30}
              value={strokeSize}
              onChange={e=>setStrokeSize(Number(e.target.value))}
            />
          </label>
          <label>
            Eraser size:
            <input
              className="size-range"
              type="range"
              min={4} max={60}
              value={eraserSize}
              onChange={e=>setEraserSize(Number(e.target.value))}
            />
          </label>
        </div>

        {/* Toolbar */}
        <div className="tool-buttons">
          <button
            className={mode==="draw"?"selected":""}
            onClick={()=>setMode("draw")}
          >✏️ Draw</button>
          <button
            className={mode==="erase"?"selected":""}
            onClick={()=>setMode("erase")}
          >🧽 Erase</button>
          <button
            className={mode==="crop"?"selected":""}
            onClick={()=>setMode("crop")}
          >✂️ Crop</button>
        </div>

        {/* Zoom (crop only) */}
        {mode==="crop" && (
          <div className="Screeenshot-zoom-slider">
            <input
              type="range" min={1} max={3} step={0.1}
              value={zoom}
              onChange={e=>setZoom(Number(e.target.value))}
            />
          </div>
        )}

        {/* Save / Cancel */}
        <div className="screenshot-editor-buttons">
          <button className={'app-cancel-control'} onClick={onCancel}>Cancel</button>
          <button onClick={handleSave}>Save to Notes</button>
        </div>
      </div>
    </div>
  );
}
